import { Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Trash, Printer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Th, Td, Tag, Confirm } from "@/components/common";
import { api, money, fmtDate, qty, errMsg } from "@/lib/api";

export function OrderItems({ d }) {
  const cur = d.currency;
  return (
    <div className="overflow-x-auto">
      <table className="mt-6 w-full" data-testid="order-items-table">
        <thead><tr><Th>Ürün</Th><Th right>Sipariş Mkt.</Th><Th right>Sevk Edilen</Th><Th right>Kalan</Th><Th right>Faturalanan</Th><Th right>Birim Fiyat</Th><Th right>KDV</Th><Th right>Tutar</Th></tr></thead>
        <tbody>
          {d.items.map((it, i) => {
            const sh = it.shipped_qty || 0;
            const rem = Math.max(it.qty - sh, 0);
            const pct = Math.min((sh / it.qty) * 100, 100);
            return (
              <tr key={i} data-testid={`order-line-${i}`}>
                <Td>{it.name}<div className="font-num text-[11px] text-muted-foreground">{it.code}</div>
                  <div className="mt-1.5 h-1 w-32 bg-muted"><div className="h-1 bg-emerald-600" style={{ width: `${pct}%` }} /></div>
                </Td>
                <Td right data-testid={`order-line-ordered-${i}`}>{qty(it.qty)} <span className="text-xs text-muted-foreground">{it.unit}</span></Td>
                <Td right className="text-emerald-700" data-testid={`order-line-shipped-${i}`}>{qty(sh)}</Td>
                <Td right className={rem > 0 ? "font-semibold text-amber-600" : "text-muted-foreground"} data-testid={`order-line-remaining-${i}`}>
                  {d.status === "closed" && rem > 0 ? <span title="Sipariş kapatıldı">{qty(rem)} (kapandı)</span> : qty(rem)}
                </Td>
                <Td right data-testid={`order-line-invoiced-${i}`}>{qty(it.invoiced_qty || 0)}</Td>
                <Td right>{money(it.price, cur)}</Td>
                <Td right>%{it.vat_rate}</Td>
                <Td right className="font-semibold">{money(it.total, cur)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const INFO = [["waybill_no", "İrsaliye No"], ["vehicle_plate", "Plaka"], ["driver", "Şoför / Teslim Eden"], ["address", "Adres"], ["notes", "Not"]];

function ShipmentCard({ s, onChanged }) {
  const del = async () => {
    try {
      await api.delete(`/shipments/${s.id}`);
      toast.success("Sevk silindi, stok geri alındı");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border-b border-border px-5 py-4 last:border-b-0" data-testid={`shipment-card-${s.no}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-num text-sm font-semibold">{s.no}</span>
        <span className="font-num text-xs text-muted-foreground">{fmtDate(s.date)}</span>
        {s.invoice_id ? <Link to={`/faturalar/${s.invoice_id}`}><Tag tone="invoiced">Faturalandı · {s.invoice_no}</Tag></Link> : <Tag tone="pending">Faturalanmadı</Tag>}
        <Button variant="outline" size="sm" className="ml-auto rounded-[4px]" onClick={() => window.open(`/irsaliye/${s.id}?print=1`, "_blank")} data-testid={`shipment-print-button-${s.no}`}>
          <Printer /> {s.kind === "sale" ? "İrsaliye Yazdır" : "Fiş Yazdır"}
        </Button>
        {!s.invoice_id && (
          <Confirm title="Sevk silinsin mi?" description="Sevk silinir, stok hareketi geri alınır ve miktar siparişe geri eklenir." onConfirm={del} testid={`shipment-delete-confirm-${s.no}`}
            trigger={<Button variant="ghost" size="icon" className="text-red-600" data-testid={`shipment-delete-button-${s.no}`}><Trash /></Button>} />
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ul className="space-y-1 text-sm">
          {s.items.map((it, i) => (
            <li key={i} className="flex justify-between gap-3 border-b border-dashed border-border pb-1">
              <span>{it.name}</span><span className="font-num font-semibold">{qty(it.qty)} {it.unit}</span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {INFO.filter(([k]) => s[k]).map(([k, l]) => (<Fragment key={k}><span className="text-muted-foreground">{l}</span><span>{s[k]}</span></Fragment>))}
          {!INFO.some(([k]) => s[k]) && <span className="col-span-2 text-muted-foreground">Ek sevk bilgisi girilmemiş</span>}
        </div>
      </div>
    </div>
  );
}

export function ShipmentList({ order, shipments, onChanged }) {
  const sale = order.kind === "sale";
  return (
    <div className="panel mt-6" data-testid="shipment-list">
      <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
        <Truck size={18} className="text-muted-foreground" />
        <h3 className="font-display text-lg font-bold">{sale ? "Sevk Detayları" : "Mal Kabul Detayları"}</h3>
        <span className="ml-auto font-num text-xs text-muted-foreground">{shipments.length} kayıt</span>
      </div>
      {shipments.length === 0 && <div className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="shipment-empty">Henüz {sale ? "sevk" : "mal kabul"} yapılmadı</div>}
      {shipments.map((s) => <ShipmentCard key={s.id} s={s} onChanged={onChanged} />)}
    </div>
  );
}
